import { useEffect, useRef } from "react";
import { useChatSocketOptional } from "../context/ChatSocketContext";
import type { AggregatedNoteDto } from "../services/vorgangNoteService";
import type { NoteLiveRef } from "../components/notes/noteLiveMerge";

/**
 * SP11 + Vg-SP7 — Echtzeit-Andockpunkt an den Socket.IO-Raum `vorgang:<id>`.
 *
 * Bei geöffnetem Vorgang-Detail tritt der (App-weite) Chat-Socket dem
 * tenant-präfixierten Vorgang-Raum bei (der Server prüft dabei das Leserecht,
 * fail-closed). Über denselben EINEN Join/Leave laufen BEIDE Frame-Familien:
 *  - SP11: `note:created|updated|removed` (volle Payload) → optimistischer
 *    Merge in die Notiz-Liste, KEIN Voll-Reload. Fällt der Socket aus, bleibt
 *    der bestehende SSE-`note`-Kanal als Fallback (Aufrufer prüft `connected`).
 *  - Vg-SP7: `vorgang:dirty` (metadatenarmes Signal) → der Aufrufer re-fetcht
 *    gebündelt über seinen vollen Leserecht-Lesepfad (Echo-Filter/Debounce dort).
 *
 * WICHTIG (Leave-Falle): `vorgang:leave` ist socket-WEIT. Notiz- und Dirty-
 * Listener MÜSSEN sich deshalb EIN gemeinsamen Join/Leave teilen — zwei getrennte
 * Hooks würden sich gegenseitig aus dem Raum werfen. Daher hier gebündelt.
 *
 * Robustheit: die Callbacks laufen über ein Ref, damit das Join/Subscribe-Effekt
 * NUR bei Socket-/Vorgang-Wechsel neu aufsetzt (nicht bei jeder Render-Runde).
 */
export interface VorgangDirtyFrame {
  vorgangId: number;
  kind: "vorgang" | "todo";
  action: string;
  todoId: number | null;
  actorUserId: string | null;
}

export interface VorgangNotesLiveHandlers {
  onCreated?: (note: AggregatedNoteDto) => void;
  onUpdated?: (note: AggregatedNoteDto) => void;
  onRemoved?: (ref: NoteLiveRef) => void;
  /** Vg-SP7: fremde/eigene Vorgang-/To-do-Änderung im Raum (Client re-fetcht). */
  onVorgangDirty?: (frame: VorgangDirtyFrame) => void;
}

type NoteFrame = { vorgangId?: number; note?: AggregatedNoteDto };
type DirtyFrame = {
  vorgangId?: number;
  kind?: string;
  action?: string;
  todoId?: number | null;
  actorUserId?: string | null;
};

export function useVorgangNotesLive(
  vorgangId: number | null | undefined,
  handlers: VorgangNotesLiveHandlers,
): { connected: boolean } {
  const socket = useChatSocketOptional();
  const connected = socket?.connected ?? false;
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!socket || !connected || !vorgangId || !Number.isFinite(vorgangId)) return;

    // Raum betreten — der Server prüft das Vorgang-Leserecht (RBAC, fail-closed);
    // ein abgelehnter Join (fremder Tenant / PRIVATE ohne Recht) wird still
    // verworfen: dann liefert schlicht der SSE-Fallback.
    void socket.emitAck("vorgang:join", { vorgangId }).catch(() => undefined);

    const matches = (p: NoteFrame): p is Required<NoteFrame> =>
      !!p && p.vorgangId === vorgangId && !!p.note;

    const offCreated = socket.on("note:created", (p: NoteFrame) => {
      if (matches(p)) handlersRef.current.onCreated?.(p.note);
    });
    const offUpdated = socket.on("note:updated", (p: NoteFrame) => {
      if (matches(p)) handlersRef.current.onUpdated?.(p.note);
    });
    const offRemoved = socket.on("note:removed", (p: NoteFrame) => {
      if (matches(p)) handlersRef.current.onRemoved?.({ source: p.note.source, id: p.note.id });
    });

    // Vg-SP7: Dirty-Signal für Vorgang-/To-do-Änderungen (KEIN eigener Join —
    // derselbe Raum wie die Notiz-Frames). Der Aufrufer entscheidet über Echo-
    // Filter/Debounce, ob und wann er neu lädt.
    const offDirty = socket.on("vorgang:dirty", (p: DirtyFrame) => {
      if (!p || p.vorgangId !== vorgangId) return;
      handlersRef.current.onVorgangDirty?.({
        vorgangId,
        kind: p.kind === "todo" ? "todo" : "vorgang",
        action: String(p.action ?? ""),
        todoId: typeof p.todoId === "number" ? p.todoId : null,
        actorUserId: typeof p.actorUserId === "string" ? p.actorUserId : null,
      });
    });

    return () => {
      offCreated();
      offUpdated();
      offRemoved();
      offDirty();
      socket.emit("vorgang:leave", { vorgangId });
    };
  }, [socket, connected, vorgangId]);

  return { connected };
}
